import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-confirm-modal',
  standalone: true,
  imports: [CommonModule, IconComponent, ButtonComponent],
  template: `
    <div
      *ngIf="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      (click)="onBackdropClick($event)"
    >
      <div
        class="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-scale-in"
        (click)="$event.stopPropagation()"
      >
        <!-- Icon -->
        <div class="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full"
          [ngClass]="iconBgClass">
          <app-icon
            [name]="icon"
            [className]="iconClass"
          ></app-icon>
        </div>

        <!-- Title -->
        <h3 class="text-lg font-bold text-center text-gray-900 mb-2">
          {{ title }}
        </h3>

        <!-- Message -->
        <p class="text-sm text-center text-gray-600 mb-6">
          {{ message }}
        </p>

        <!-- Actions -->
        <div class="flex gap-3">
          <button
            app-button
            variant="outline"
            size="md"
            (click)="onCancel()"
            class="flex-1"
          >
            {{ cancelText }}
          </button>
          <button
            app-button
            [className]="confirmButtonClass"
            size="md"
            (click)="onConfirm()"
            class="flex-1"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @keyframes scale-in {
      from {
        transform: scale(0.9);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }

    .animate-scale-in {
      animation: scale-in 0.2s ease-out;
    }
  `]
})
export class ConfirmModalComponent {
  @Input() isOpen = false;
  @Input() title = 'Confirmar ação';
  @Input() message = 'Tem certeza que deseja continuar?';
  @Input() confirmText = 'Confirmar';
  @Input() cancelText = 'Cancelar';
  @Input() type: 'danger' | 'warning' | 'info' | 'success' = 'danger';

  @Output() confirm = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  get icon(): string {
    const icons = {
      danger: 'alert-circle',
      warning: 'alert-triangle',
      info: 'info',
      success: 'check-circle'
    };
    return icons[this.type];
  }

  get iconClass(): string {
    const classes = {
      danger: 'w-6 h-6 text-red-600',
      warning: 'w-6 h-6 text-yellow-600',
      info: 'w-6 h-6 text-blue-600',
      success: 'w-6 h-6 text-green-600'
    };
    return classes[this.type];
  }

  get iconBgClass(): string {
    const classes = {
      danger: 'bg-red-100',
      warning: 'bg-yellow-100',
      info: 'bg-blue-100',
      success: 'bg-green-100'
    };
    return classes[this.type];
  }

  get confirmButtonClass(): string {
    const classes = {
      danger: 'bg-red-600 hover:bg-red-700 text-white',
      warning: 'bg-yellow-600 hover:bg-yellow-700 text-white',
      info: 'bg-blue-600 hover:bg-blue-700 text-white',
      success: 'bg-green-600 hover:bg-green-700 text-white'
    };
    return classes[this.type];
  }

  onConfirm(): void {
    this.confirm.emit();
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }
}
